/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-non-null-assertion -- DIRECT-239681 */

import { AnyFn, keys, objectMap } from '@direct-frontend/stdlib';
import {
    buildCreateApi as buildRTKQueryCreateApiRedux,
    coreModule,
    reactHooksModule,
    setupListeners,
} from '@reduxjs/toolkit/query/react';

import {
    AnyEndpointDefinition,
    apiSSRModule,
    EndpointsMethodsAndHooks,
    EndpointsRoot,
    MutationEndpointMethodsAndHooks,
    QueryEndpointMethodsAndHooks,
} from './apiSSRModule';
import { bindActionCreator, BoundActionCreator } from './bindActionCreator';
import { createSetupListenersHandler } from './createSetupListenersHandler';
import { buildCreateLegacyEndpointDataProvider } from './endpointDataProvider';
import { buildCreateEndpointDataProvider } from './endpointDataProvider';
import { errorRetryMiddleware } from './errorRetryMiddleware';
import { createHydrateApiAction, extractReducerRehydrationInfo, subscribeToHydratedApiData } from './hydration';
import { resetAllApisMiddleware } from './resetAllApisMiddleware';
import { patchApiReducer } from './patchApiReducer';
import type { Reducer, Store } from './rtk-types';
import type {
    Api,
    BaseQueryFn,
    BaseQueryMeta,
    CombinedState,
    CoreModule,
    CreateApiOptions as CreateApiOptionsRedux,
    EndpointDefinitions,
    MutationDefinition,
    QueryArgFrom,
    QueryDefinition,
    QueryReturnValue,
    UpdateDefinitions,
} from './rtk-query-types';
import type {
    GetStoreFn,
    InjectMiddlewareFn,
    InjectReducerFn,
    UseDispatchType,
    UseSelectorType,
    UseStoreType,
} from './types';
import { validateKeepUnusedDataFor } from './utils';
import {
    subscribeToGlobalInvalidationTags,
    addPublishGlobalInvalidationTagsToEndpoints,
    setupGlobalInvalidationTags,
} from './globalInvalidationTags';

type ReactRedux = typeof import('react-redux');

/**
 * Символ для приватного хранения внутренностей созданного API в возвращаемом значении `createApi`.
 * Внутренности нужны для более удобного выведения типов в дженериках.
 */
export const $INTERNALS = Symbol('$INTERNALS');

/**
 * Набор модулей-расширений RTK Query, использующихся в модульном `createApi()`.
 */
export type ApiModules =
    | CoreModule
    | ReturnType<typeof reactHooksModule>['name']
    | ReturnType<typeof apiSSRModule>['name'];

/**
 * Произвольный созданный RTK Query API.
 * Используется в условных типах и дженериках как правая часть extends.
 *
 * @internal
 */
export type AnyApi = Api<any, any, any, any, ApiModules>;

// Набор допустимых имен методов в корне RTK Query эндпоинта.
type PossibleRootName = `${string}Query` | `${string}Mutation`;

// Проверяет является ли строка допустимым именем для корня RTK Query эндпоинта.
type IsRootName<Name extends string> = Name extends PossibleRootName ? Name : never;

/**
 * Проверяет является ли строка допустимым именем для RTK Query эндпоинта.
 *
 * @param value – Имя, которое нужно проверить.
 * @returns true, если строка является допустимым именем RTK Query хука.
 */
function isPossibleRootName<Api extends AnyApi>(value: string): value is IsRootName<string & keyof Api> {
    const isQueryMethod = value.endsWith('Query');
    const isMutationMethod = value.endsWith('Mutation');

    return isQueryMethod || isMutationMethod;
}

/**
 * Достает из RTK Query API все методы, которые попадают в корень эндпоинта.
 *
 * @param api – RTK Query API.
 * @returns Все методы созданного RTK Query API, которые попадают в корень эндпоинта.
 */
function extractEndpointRoot<Api extends AnyApi>(api: Api) {
    return keys(api).reduce<Partial<Api>>((acc, key) => {
        if (isPossibleRootName<Api>(key)) {
            acc[key] = api[key];
        }

        return acc;
    }, {});
}

/** Выводит тип дефинишенов API */
type InferDefinitions<A> = A extends Api<any, infer Definitions, any, any, any> ? Definitions : never;

// Параметры функции `bindEndpoints()`
type BindEndpointsOptions<Api extends AnyApi> = {
    api: Api;
    getStore: GetStoreFn;
    useSelector: UseSelectorType;
    util: BoundUtil<Api>;
    definitions: InferDefinitions<Api>;
};

/**
 * Привязывает все эндпоинты RTK Query API к стору.
 *
 * @param getStore - Функция, возвращающая стор, к которому будет привязан метод.
 * @param api – RTK Query API.
 * @returns Привязанные к стору эндпоинты, методы которых можно вызывать без `store.dispatch()`.
 */
function bindEndpoints<Api extends AnyApi>({ api, definitions }: BindEndpointsOptions<Api>) {
    type Definitions = InferDefinitions<Api>;
    type BoundEndpoints = EndpointsMethodsAndHooks<Definitions>;

    return keys(api.endpoints).reduce((acc, key: keyof BoundEndpoints) => {
        const endpoint = api.endpoints[key]!;

        if ('query' in endpoint) {
            const boundEndpoint: QueryEndpointMethodsAndHooks<any> = {
                getCachedValue: endpoint.getCachedValue,
                useCachedValues: endpoint.useCachedValues,
                query: endpoint.query,
                queryFlow: endpoint.queryFlow,
                prefetch: endpoint.prefetch,
                subscribe: endpoint.subscribe,
                useQuery: endpoint.useQuery,
                useLazyQuery: endpoint.useLazyQuery,
                useQueryState: endpoint.useQueryState,
                updateQueryData: endpoint.updateQueryData,
                upsertQueryData: endpoint.upsertQueryData,
                patchQueryData: endpoint.patchQueryData,
                createLegacyDataProvider: buildCreateLegacyEndpointDataProvider(
                    {
                        name: endpoint.name,
                        useCachedValues: endpoint.useCachedValues,
                        subscribe: endpoint.subscribe,
                        useQuery: endpoint.useQuery,
                        useQueryState: endpoint.useQueryState,
                        updateQueryData: endpoint.updateQueryData,
                    },
                    definitions[endpoint.name]
                ),
                createDataProvider: buildCreateEndpointDataProvider(
                    {
                        name: endpoint.name,
                        queryFlow: endpoint.queryFlow,
                        updateQueryData: endpoint.updateQueryData,
                    },
                    definitions[endpoint.name]
                ),
            };

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            acc[key] = boundEndpoint as BoundEndpoints[string];
        }

        if ('mutate' in endpoint) {
            const boundEndpoint: MutationEndpointMethodsAndHooks<any> = {
                getCachedValue: endpoint.getCachedValue,
                useCachedValues: endpoint.useCachedValues,
                mutate: endpoint.mutate,
                mutationState: endpoint.mutationState,
            };

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            acc[key] = boundEndpoint as BoundEndpoints[string];
        }

        return acc;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    }, {} as BoundEndpoints);
}

// Список утилитарных методов RTK Query API, которые должны быть привязаны к стору.
type BoundUtilMethods = 'invalidateTags' | 'updateQueryData' | 'upsertQueryData' | 'patchQueryData' | 'resetApiState';

/**
 * Утилитарные методы RTK Query API, привязанные к стору.
 *
 * @typeParam Api – Тип созданного RTK Query API.
 */
type BoundUtil<Api> = Api extends { util: Record<BoundUtilMethods, AnyFn> }
    ? {
          invalidateTags: BoundActionCreator<Api['util']['invalidateTags']>;
          resetApiState: BoundActionCreator<Api['util']['resetApiState']>;
      }
    : never;

/**
 * Привязывает утилитарные методы RTK Query API к стору.
 *
 * @param getStore – Функция, возвращающая стор, к которому будут привязаны утилитарные методы.
 * @param api – RTK Query API.
 * @returns Обернутые утилитарные методы RTK Query, которые можно вызывать без `store.dispatch()`.
 */
function bindUtil(getStore: () => Store, api: AnyApi): BoundUtil<AnyApi> {
    return {
        invalidateTags: bindActionCreator(getStore, api.util.invalidateTags),
        resetApiState: bindActionCreator(getStore, api.util.resetApiState),
    };
}

/**
 * Получает тип возвращаемого значения для дефинишена эндпоинта
 *
 * @typeParam Definition – Дефинишен эндпоинта из параметров `createApi()`
 */
type DefinitionResult<Definition> = Definition extends QueryDefinition<any, any, any, infer R, any> ? R : never;

/**
 * Возвращает тип стейта RTK Query API по результату выполнения `createApi()`.
 *
 * @typeParam T – Созданный API, полученный из `createApi()`.
 * @internal
 */
export type ApiState<T> = T extends { [$INTERNALS]: { api: infer Api } }
    ? Api extends { reducerPath: infer ReducerPath; reducer: Reducer<infer State> }
        ? ReducerPath extends string
            ? { [Key in ReducerPath]: State }
            : never
        : never
    : never;

/**
 * Параметры для модификации сгенерированного API.
 *
 * @typeParam Definitions Определения эндпоинтов
 * @typeParam TagTypes Тип тегов
 * @typeParam NewTagTypes Тип новых тегов
 */
export type EnhanceEndpointsOptions<
    Definitions extends EndpointDefinitions,
    NewDefinitions extends EndpointDefinitions = never,
    TagTypes extends string = never,
    NewTagTypes extends string = never,
> = {
    addTagTypes?: readonly NewTagTypes[];
    endpoints?: UpdateDefinitions<
        Definitions,
        TagTypes | NoInfer<NewTagTypes>,
        NewDefinitions
    > extends infer InferredNewDefinitions
        ? {
              [K in keyof InferredNewDefinitions]?: Partial<InferredNewDefinitions[K]>;
          }
        : never;
};

/**
 * Произвольный маппер, который можно передать в transformResponse.
 * Используется в условных типах и дженериках как правая часть extends.
 */
type AnyMapper<BaseQuery extends BaseQueryFn, Definition extends AnyEndpointDefinition> = (
    backendResult: DefinitionResult<Definition>,
    meta: BaseQueryMeta<BaseQuery>,
    queryArg: QueryArgFrom<Definition>
) => any;

/**
 * Набор произвольный мапперов для эндпоинтов, который можно передать в transformResponse.
 * Используется в условных типах и дженериках как правая часть extends.
 */
type AnyMappersMap<BaseQuery extends BaseQueryFn, Definitions extends EndpointDefinitions> = {
    [Key in keyof Definitions]?: AnyMapper<BaseQuery, Definitions[Key]>;
};

// Заменяет тип возвращаемого значения в дефинишене эндпоинта.
type ReplaceDefinitionResult<Definition extends AnyEndpointDefinition, NewResultType> =
    Definition extends QueryDefinition<infer QueryArg, infer BaseQuery, infer TagTypes, any, infer ReducerPath>
        ? QueryDefinition<QueryArg, BaseQuery, TagTypes, NewResultType, ReducerPath>
        : Definition extends MutationDefinition<infer QueryArg, infer BaseQuery, infer TagTypes, any, infer ReducerPath>
          ? MutationDefinition<QueryArg, BaseQuery, TagTypes, NewResultType, ReducerPath>
          : never;

// Заменяет тип возвращаемых значений в дефинишенах эндпоинтов согласно набору мапперов.
type ReplaceDefinitionsResults<
    BaseQuery extends BaseQueryFn,
    Definitions extends EndpointDefinitions,
    Mappers extends AnyMappersMap<BaseQuery, Definitions>,
> = {
    [Key in keyof Definitions]: Key extends keyof Mappers
        ? ReplaceDefinitionResult<
              Definitions[Key],
              Mappers[Key] extends AnyMapper<BaseQuery, Definitions[Key]> ? Awaited<ReturnType<Mappers[Key]>> : never
          >
        : Definitions[Key];
};

/**
 * Тип созданного API, полученного из `createApi()`.
 *
 * @typeParam BaseQuery Сетевой клиент для выполнения запросов
 * @typeParam Definitions Определения эндпоинтов
 * @typeParam ReducerPath Путь в redux сторе
 * @typeParam TagTypes Тип тегов
 * @internal
 */
export type CreatedApi<
    BaseQuery extends BaseQueryFn,
    Definitions extends EndpointDefinitions,
    ReducerPath extends string,
    TagTypes extends string = never,
> = EndpointsRoot<Definitions> & {
    type: 'api';
    endpoints: EndpointsMethodsAndHooks<Definitions>;
    util: BoundUtil<Api<BaseQuery, Definitions, ReducerPath, TagTypes, ApiModules>>;
    enhanceEndpoints: <NewTagTypes extends string = never, NewDefinitions extends EndpointDefinitions = never>(
        options: EnhanceEndpointsOptions<Definitions, NewDefinitions, TagTypes, NewTagTypes>
    ) => Omit<
        CreatedApi<
            BaseQuery,
            UpdateDefinitions<Definitions, TagTypes | NewTagTypes, NewDefinitions>,
            ReducerPath,
            TagTypes | NewTagTypes
        >,
        // Исключаем transformResponse из типов после вызова enhanceEndpoints, чтобы защититься от возможных ошибок
        // Метод transformResponse меняет тип выходного значения, на которое может опираться enhanceEndpoints
        // Поэтому transformResponse должен быть вызван первым
        'transformResponse'
    >;
    transformResponse: <Mappers extends AnyMappersMap<BaseQuery, Definitions>>(
        mappers: Mappers
    ) => CreatedApi<BaseQuery, ReplaceDefinitionsResults<BaseQuery, Definitions, Mappers>, ReducerPath, TagTypes>;
    [$INTERNALS]: {
        api: Api<BaseQuery, Definitions, ReducerPath, TagTypes, ApiModules>;
        apiName: string;
        moduleName: string;
        getStore: GetStoreFn;
    };
};

/**
 * Произвольный созданный API.
 * Используется в условных типах и дженериках как правая часть extends.
 *
 * @internal
 */
export type AnyCreatedApi = CreatedApi<any, any, any, any>;

// Параметры для модульного `createApi()`.
export type InternalCreateApiOptions<
    BaseQuery extends BaseQueryFn,
    Definitions extends EndpointDefinitions,
    ReducerPath extends string,
    TagTypes extends string = never,
> = {
    name?: ReducerPath;
    baseQuery: CreateApiOptionsRedux<BaseQuery, Definitions, ReducerPath, TagTypes>['baseQuery'];
    endpoints: CreateApiOptionsRedux<BaseQuery, Definitions, ReducerPath, TagTypes>['endpoints'];
    keepUnusedDataFor?: CreateApiOptionsRedux<BaseQuery, Definitions, ReducerPath, TagTypes>['keepUnusedDataFor'];
    refetchOnMountOrArgChange?: CreateApiOptionsRedux<
        BaseQuery,
        Definitions,
        ReducerPath,
        TagTypes
    >['refetchOnMountOrArgChange'];
    refetchOnFocus?: CreateApiOptionsRedux<BaseQuery, Definitions, ReducerPath, TagTypes>['refetchOnFocus'];
    refetchOnReconnect?: CreateApiOptionsRedux<BaseQuery, Definitions, ReducerPath, TagTypes>['refetchOnReconnect'];
    tagTypes?: CreateApiOptionsRedux<BaseQuery, Definitions, ReducerPath, TagTypes>['tagTypes'];
};

// Параметры для `buildCreateSliceOptions()`.
type BuildCreateApiOptions<ModuleName extends string> = {
    moduleName: ModuleName;
    getStore: GetStoreFn;
    injectMiddleware: InjectMiddlewareFn;
    injectReducer: InjectReducerFn;
    useDispatch: UseDispatchType;
    useSelector: UseSelectorType;
    useStore: UseStoreType;
};

/**
 * Функция для создания API в модульном сторе.
 *
 * @typeParam ModuleName Название модуля
 * @internal
 */
export type BuiltCreateApi<ModuleName extends string> = <
    BaseQuery extends BaseQueryFn,
    Definitions extends EndpointDefinitions,
    Name extends string = 'api',
    TagTypes extends string = never,
>(
    options: InternalCreateApiOptions<BaseQuery, Definitions, Name, TagTypes>
) => CreatedApi<BaseQuery, Definitions, `${ModuleName}/${Name}`, TagTypes>;

/**
 * Создает модульный метод `createApi()`.
 *
 * Модульный `createApi()`:
 *  1. Создает RTK Query API с нужными модулями
 *  2. Добавляет редьюсер в модульный стор
 *  3. Добавляет мидлвару в модульный стор
 *  4. Привязывает все эндпоинты и утилитарные методы к модульному стору
 *
 * @internal
 */
export function buildCreateApi<ModuleName extends string>({
    moduleName,
    getStore,
    injectMiddleware,
    injectReducer,
    useDispatch,
    useSelector,
    useStore,
}: BuildCreateApiOptions<ModuleName>): BuiltCreateApi<ModuleName> {
    const createApi = buildRTKQueryCreateApiRedux(
        coreModule(),
        /**
         * @deprected Хуки будут удалены в пользу endpoint.query() метода и потоков
         */
        reactHooksModule({
            hooks: {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                useDispatch: useDispatch as ReactRedux['useDispatch'],
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                useSelector: useSelector as ReactRedux['useSelector'],
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
                useStore: useStore as unknown as ReactRedux['useStore'],
            },
        }),
        apiSSRModule({ getStore, useSelector })
    );

    /**
     * Создает API в модульном сторе.
     *
     * @param options – Параметры для создания API.
     * @returns API, подключенный к модульному стору. Содержит сгенерированные хуки, эндпоинты и утилитарные методы.
     * @public
     */
    return <
        BaseQuery extends BaseQueryFn,
        Definitions extends EndpointDefinitions,
        Name extends string = 'api',
        TagTypes extends string = never,
    >({
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        name = 'api' as Name,
        ...options
    }: InternalCreateApiOptions<BaseQuery, Definitions, Name, TagTypes>) => {
        const reducerPath = `${moduleName}/${name}` as const;
        type ReducerPath = typeof reducerPath;

        type ApiState = CombinedState<NoInfer<Definitions>, NoInfer<TagTypes>, NoInfer<ReducerPath>>;
        const hydrateApiAction = createHydrateApiAction<ApiState>(moduleName, name);

        if (process.env.NODE_ENV !== 'production') {
            validateKeepUnusedDataFor(options.keepUnusedDataFor);

            // eslint-disable-next-line @adv-frontend/config/pretty-todo-comments -- DIRECT-239681
            // TODO: DIRECT-163893
            if (options.refetchOnMountOrArgChange) {
                throw new Error('Опция refetchOnMountOrArgChange не поддерживается в модульных сторах.');
            }
        }

        const definitions = options.endpoints({
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            query: definition => ({ type: 'query', ...definition }) as any,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            mutation: definition => ({ type: 'mutation', ...definition }) as any,
        });

        // Добавляем в endpoint для mutation вызов события инвалидации глобальных тегов
        options.endpoints = addPublishGlobalInvalidationTagsToEndpoints(definitions, options.tagTypes, reducerPath);

        const api = createApi({
            // Кастуем к такому же типу как и был, только с новым ReducerPath, для правильного выведения типов далее.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            ...(options as InternalCreateApiOptions<BaseQuery, Definitions, ReducerPath, TagTypes>),

            // Префикс экшенов должен содержать moduleName, чтобы их можно было различать в «App Store».
            reducerPath,

            /**
             * Гидрация данных RTK Query API работает через встроенный механизм `extractRehydrationInfo`.
             * Под капотом данные с сервера очищаются и подготавливаются для гидрации на клиенте.
             */
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
            // @ts-expect-error DIRECT-235591 TS7030
            extractRehydrationInfo(action) {
                if (action && hydrateApiAction.match(action)) {
                    return action.payload;
                }
            },
        });

        /**
         * RTK Query плохо выводит типы при создании API с кастомными модулями, из-за чего в типе api
         * есть только injectEndpoints и enhanceEndpoints. Кастуем api к AnyApi, чтобы получить правильный тип,
         * с которым будет удобно работать.
         */
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const anyApi = api as unknown as AnyApi;

        injectMiddleware(resetAllApisMiddleware(anyApi));
        injectMiddleware(errorRetryMiddleware(anyApi));
        injectMiddleware(anyApi.middleware);
        injectReducer(reducerPath, patchApiReducer(anyApi, definitions));

        if (process.env.BUILD_TARGET === 'client') {
            // Вызываем гидрацию слайса на клиенте.
            const hydratedData = extractReducerRehydrationInfo(moduleName, reducerPath);
            if (hydratedData) {
                getStore().dispatch(hydrateApiAction(hydratedData));
                subscribeToHydratedApiData(anyApi, hydratedData);
            }

            // Подписываемся на события фокуса и статуса сети, чтобы поддержать refetchOnFocus и refetchOnReconnect.
            setupListeners((action: any) => getStore().dispatch(action), createSetupListenersHandler(reducerPath));

            // Подписываемся на событие инвалидации глобальных тегов
            subscribeToGlobalInvalidationTags(anyApi, definitions, getStore, options.tagTypes, reducerPath);
        }

        return exportApi<BaseQuery, Definitions, ReducerPath, TagTypes>({
            api: anyApi,
            apiName: name,
            definitions,
            getStore,
            moduleName,
            useSelector,
        });
    };
}

// Параметры для `exportApi()`.
type ExportApiOptions<Definitions extends EndpointDefinitions> = {
    api: AnyApi;
    apiName: string;
    definitions: Definitions;
    getStore: GetStoreFn;
    moduleName: string;
    useSelector: UseSelectorType;
};

/**
 * Функция подготовки публичного api для RTK Query, доступного для использования во всех модулях.
 *
 * @param getStore Получение стора
 * @param anyApi Инстансу RTK Query
 * @returns Публичное api
 */
function exportApi<
    BaseQuery extends BaseQueryFn,
    Definitions extends EndpointDefinitions,
    ReducerPath extends string,
    TagTypes extends string,
>({ api: anyApi, apiName, definitions, getStore, moduleName, useSelector }: ExportApiOptions<Definitions>) {
    /**
     * RTK Query плохо выводит типы при создании API с кастомными модулями, из-за чего в типе api
     * есть только injectEndpoints и enhanceEndpoints. Поэтому функция принимает AnyApi, который вручную
     * приводим к целевому типу API.
     */
    type ApiType = Api<BaseQuery, Definitions, ReducerPath, TagTypes, ApiModules>;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const api = anyApi as unknown as ApiType;

    /**
     * Так как все манипуляции происходят с anyApi, то TypeScript не сможет вывести типы, содержащие
     * настоящие названия хуков и эндпоинтов. Кастуем полученные хуки и методы к правильным типам,
     * основанным на настоящем api.
     */
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const root = extractEndpointRoot(anyApi) as unknown as EndpointsRoot<Definitions>;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const util = bindUtil(getStore, anyApi) as unknown as BoundUtil<typeof api>;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoints = bindEndpoints({
        api: anyApi,
        getStore,
        useSelector,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        util: util as unknown as BoundUtil<typeof anyApi>,
        definitions,
    }) as unknown as EndpointsMethodsAndHooks<Definitions>;

    /**
     * Метод для модификации сгенерированного API. Вызывает метод enhanceEndpoints на созданном RTK Query API
     * и возвращает новые хуки, эндпоинты и методы.
     */
    function enhanceEndpoints<NewTagTypes extends string = never, NewDefinitions extends EndpointDefinitions = never>(
        options: EnhanceEndpointsOptions<Definitions, NewDefinitions, TagTypes, NewTagTypes>
    ) {
        type EnhancedTagTypes = TagTypes | NewTagTypes;
        type EnhancedDefinitions = UpdateDefinitions<Definitions, EnhancedTagTypes, NewDefinitions>;

        if (process.env.NODE_ENV !== 'production' && options.endpoints) {
            keys(options.endpoints).forEach(endpointName => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
                // @ts-expect-error Параметр на самом деле есть, но в типах не выводится
                validateKeepUnusedDataFor(options.endpoints[endpointName].keepUnusedDataFor);
            });
        }

        // Подписываемся и вызываем событие инвалидации кеша для глобальных тегов
        setupGlobalInvalidationTags(anyApi, getStore, options);

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const enhancedApi = api.enhanceEndpoints(options) as unknown as AnyApi;

        // Мутируем существующий `definitions` теми же полями, что добавил RTK Query внутри
        // `context.endpointDefinitions` (см. `@reduxjs/toolkit/src/query/createApi.ts`, обработка
        // `enhanceEndpoints.endpoints`). Это нужно, чтобы замыкания, захватившие `definitions`
        // до вызова `enhanceEndpoints` (в частности `patchApiReducer`, отвечающий за выставление
        // `invalidatedBy` в том же api), увидели свежий `invalidatesTags`.
        if (options.endpoints) {
            for (const endpointName of keys(options.endpoints)) {
                const partial = options.endpoints[endpointName];
                const existing = definitions[endpointName];

                if (!existing || !partial) {
                    continue;
                }

                if (typeof partial === 'function') {
                    partial(existing);
                } else {
                    Object.assign(existing, partial);
                }
            }
        }

        return exportApi<BaseQuery, EnhancedDefinitions, ReducerPath, EnhancedTagTypes>({
            api: enhancedApi,
            apiName,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            definitions: definitions as unknown as EnhancedDefinitions,
            getStore,
            moduleName,
            useSelector,
        });
    }

    /**
     * Метод для модификации формата ответов эндпоинтов в сгенерированном API. Вызывает метод enhanceEndpoints на
     * созданном RTK Query API и возвращает новый API с правильными типами.
     */
    function transformResponse<Mappers extends AnyMappersMap<BaseQuery, Definitions>>(mappers: Mappers) {
        type EnhancedDefinitions = ReplaceDefinitionsResults<BaseQuery, Definitions, Mappers>;

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        const enhancedApi = anyApi.enhanceEndpoints({
            endpoints: objectMap(mappers, (transformResponse, endpointKey) => {
                const definition = definitions[endpointKey]!;

                /** queryFn не подхватывает transformResponse, поэтому оборачиваем руками */
                if (definition.queryFn) {
                    return {
                        queryFn: async (
                            ...args: Parameters<typeof definition.queryFn>
                        ): Promise<QueryReturnValue<any, any, any>> => {
                            const queryResult = await definition.queryFn(...args);

                            if (queryResult.data) {
                                return {
                                    ...queryResult,
                                    data:
                                        (await transformResponse?.(queryResult.data, queryResult.meta, args[0])) ??
                                        queryResult.data,
                                };
                            }

                            return queryResult;
                        },
                    };
                }

                return { transformResponse };
            }),
        }) as unknown as AnyApi;

        // Мутируем существующий `definitions` in-place, чтобы не рвать ссылку, захваченную в
        // замыканиях (в частности `patchApiReducer`). Если бы здесь создавался новый объект,
        // последующий `.enhanceEndpoints` после `.transformResponse` работал бы с копией, а не
        // с тем же `definitions`, который видит редьюсер, — и invalidatedBy не выставлялся бы.
        for (const key of keys(mappers)) {
            const mapper = mappers[key];
            const definition = definitions[key];

            if (!definition || mapper === undefined) {
                continue;
            }

            definition.transformResponse = mapper;
        }

        return exportApi<BaseQuery, EnhancedDefinitions, ReducerPath, TagTypes>({
            api: enhancedApi,
            apiName,
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            definitions: definitions as EnhancedDefinitions,
            getStore,
            moduleName,
            useSelector,
        });
    }

    const createdApi: CreatedApi<BaseQuery, Definitions, ReducerPath, TagTypes> = {
        ...root,
        type: 'api',
        endpoints,
        util,
        // Приводим тип, так как после вызова enhanceEndpoints мы исключаем transformResponse
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        enhanceEndpoints: enhanceEndpoints as unknown as CreatedApi<
            BaseQuery,
            Definitions,
            ReducerPath,
            TagTypes
        >['enhanceEndpoints'],
        transformResponse,
        [$INTERNALS]: {
            api,
            apiName,
            moduleName,
            getStore,
        },
    };

    return createdApi;
}
